import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Home, Mail, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

export const ChaletGeminiPage = () => {
  const navigate = useNavigate();

  const images = [
    "https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/xjj2xjdt_IMMOCONCEPT_RIEME_VAL_BAGNES_EXT_NUIT_MAJ_HD.jpg",
    "https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/y0j095xe_IMMOCONCEPT_RIEME_VAL_BAGNES_SEJOUR_HD.jpg",
    "https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/2hloet6w_IMMOCONCEPT_RIEME_VAL_BAGNES_SEJOUR_CLOSUP_A.jpg",
    "https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/1p1amqsf_IMMOCONCEPT_RIEME_VAL_BAGNES_CHAMBRE_HD.jpg",
    "https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/fp8zu8z2_IMMOCONCEPT_RIEME_VAL_BAGNES_SEJOUR_CLOSUP_B.jpg",
    "https://customer-assets.emergentagent.com/job_gemini-alpine/artifacts/cu7rhqpv_IMMOCONCEPT_RIEME_VAL_BAGNES_SPA_HD.jpg"
  ];

  const caracteristiques = [
    { label: "Surface USPI", value: "~270 m²" },
    { label: "Séjour/Cuisine", value: "~100 m²" },
    { label: "Chambres", value: "3 chambres" },
    { label: "Salles de bains", value: "2 + WC visiteur" },
    { label: "Type", value: "Chalets mitoyens" },
    { label: "Label", value: "Minergie" },
    { label: "Bien-être", value: "Spa, Jacuzzi, Sauna" }
  ];

  const prestations = [
    "Finition haut de gamme avec matériaux nobles",
    "Label Minergie pour une efficacité énergétique optimale",
    "Spa privatif avec jacuzzi",
    "Cheminée, ascenseur, cave et buanderie",
    "Garage couvert et deux places extérieures inclus dans le prix de vente",
    "Orientation plein sud avec panorama exceptionnel sur les montagnes",
    "À 5 minutes en voiture du centre de Verbier"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1e5a7a] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-[#f5b800] mb-6"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour à l'accueil
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Chalet Gemini 1308
          </h1>
          <div className="flex flex-wrap gap-4 text-lg">
            <div className="flex items-center">
              <MapPin className="mr-2" size={20} />
              Verbier, Médières
            </div>
            <div className="flex items-center">
              <Home className="mr-2" size={20} />
              Deux chalets mitoyens d'exception
            </div>
          </div>
        </div>
      </div>

      {/* Carrousel d'images */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Carousel className="w-full">
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="relative h-[500px] md:h-[600px] overflow-hidden rounded-2xl">
                    <img
                      src={image}
                      alt={`Chalet Gemini 1308 - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-4" />
            <CarouselNext className="right-4" />
          </Carousel>
        </div>
      </section>

      {/* Description */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-8">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Un Chalet d'Exception
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Situé au cœur du prestigieux domaine de Verbier dans le village de Médières, le projet <strong>Chalet Gemini 1308</strong> se compose de deux chalets mitoyens d'exception, alliant le charme du vieux bois et de la pierre à une architecture contemporaine alpine raffinée.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Implanté plein sud, dans un environnement calme et préservé à seulement 5 minutes en voiture du centre de Verbier, le projet bénéficie d'un panorama exceptionnel sur les montagnes et d'un ensoleillement optimal tout au long de la journée.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Aménagement Intérieur
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Chaque chalet offre une surface d'environ <strong>270 m² USPI</strong>, comprenant :
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <CheckCircle size={24} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-lg">Un séjour et une cuisine ouverte d'environ 100 m²</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={24} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-lg">Une suite parentale avec salle de bains attenante</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={24} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-lg">Deux chambres supplémentaires</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle size={24} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-lg">Une salle de bains séparée ainsi qu'un WC visiteur</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Prestations Haut de Gamme
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Les prestations sont à la hauteur des plus hautes exigences :
                  </p>
                  <ul className="space-y-3">
                    {prestations.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle size={24} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-lg">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-700 mt-8 leading-relaxed text-lg">
                    Conçu pour un usage en résidence principale, le <strong>Chalet Gemini 1308</strong> allie confort, élégance et authenticité dans un cadre naturel d'exception.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white sticky top-8">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-[#f5b800]">
                    Informations
                  </h3>
                  <div className="space-y-4 mb-8">
                    {caracteristiques.map((item, index) => (
                      <div key={index} className="border-b border-white/20 pb-3">
                        <p className="text-sm text-white/70 mb-1">{item.label}</p>
                        <p className="text-lg font-semibold">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/20">
                    <h4 className="font-semibold mb-3 text-[#f5b800]">Contact</h4>
                    <div className="flex items-start">
                      <Mail className="mr-2 flex-shrink-0 mt-1" size={18} />
                      <a href="mailto:guillaume@immoconceptsuisse.com" className="text-white hover:text-[#f5b800] transition-colors break-all">
                        guillaume@immoconceptsuisse.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Intéressé par ce projet ?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contactez-nous pour plus d'informations ou pour planifier une visite.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/#contact')}
              size="lg"
              className="bg-[#f5b800] hover:bg-[#d9a400] text-black font-semibold px-8 py-6 text-lg"
            >
              Nous contacter
            </Button>
            <Button
              onClick={() => window.open('mailto:guillaume@immoconceptsuisse.com', '_blank')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1e5a7a] font-semibold px-8 py-6 text-lg"
            >
              Envoyer un email
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};